import { useEffect } from 'react';
import { clsx } from 'clsx';
import { Loader2, Wand2, AlertCircle } from 'lucide-react';
import { useResolutionPipelineStore } from '@/stores/resolutionPipeline';

const GENERIC_LABELS = new Set([
  'related_to',
  'RELATED_TO',
  'drives',
  'DRIVES',
]);

export function EdgeQualityPanel() {
  const {
    edgeCounts,
    edgesLoading,
    retypeBusy,
    retypeStatus,
    fetchEdgeCounts,
    retypeGenericEdges,
  } = useResolutionPipelineStore();

  useEffect(() => {
    fetchEdgeCounts();
  }, [fetchEdgeCounts]);

  const total = edgeCounts.reduce((sum, e) => sum + e.count, 0);
  const genericCount = edgeCounts
    .filter((e) => GENERIC_LABELS.has(e.relation_type))
    .reduce((s, e) => s + e.count, 0);
  const maxCount = edgeCounts[0]?.count ?? 0;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-navy-900">Relation Types</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {total.toLocaleString()} total edges · {edgeCounts.length} distinct
              types{genericCount > 0 && ` · ${genericCount.toLocaleString()} generic (retype candidates)`}
            </p>
          </div>
          <button
            onClick={retypeGenericEdges}
            disabled={retypeBusy}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold bg-brand-teal text-navy-900 rounded-lg hover:bg-brand-teal/90 disabled:opacity-40"
          >
            {retypeBusy ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Wand2 size={12} />
            )}
            Retype Generic Edges
          </button>
        </div>
        {retypeStatus && (
          <div className="flex items-center gap-2 text-xs bg-brand-teal/5 border border-brand-teal/10 rounded-lg px-3 py-2 text-brand-teal-dark mb-3">
            <AlertCircle size={12} />
            {retypeStatus}
          </div>
        )}

        {edgesLoading && edgeCounts.length === 0 ? (
          <div className="text-sm text-slate-400 text-center py-6">
            <Loader2 size={14} className="animate-spin inline mr-2" /> Loading
            relation counts...
          </div>
        ) : edgeCounts.length === 0 ? (
          <div className="text-sm text-slate-400 text-center py-6">
            No canonical edges recorded yet.
          </div>
        ) : (
          <div className="space-y-1.5">
            {edgeCounts.map((e) => {
              const pct = maxCount > 0 ? (e.count / maxCount) * 100 : 0;
              const isGeneric = GENERIC_LABELS.has(e.relation_type);
              return (
                <div
                  key={e.relation_type}
                  className="flex items-center gap-3 text-xs"
                >
                  <span
                    className={clsx(
                      'w-64 truncate font-mono',
                      isGeneric ? 'text-amber-700' : 'text-slate-700',
                    )}
                  >
                    {e.relation_type}
                    {isGeneric && (
                      <span className="ml-1.5 text-[9px] font-bold bg-amber-50 text-amber-700 border border-amber-200 px-1 py-0.5 rounded uppercase">
                        generic
                      </span>
                    )}
                  </span>
                  <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                    <div
                      className={clsx(
                        'h-full rounded-full transition-all duration-500',
                        isGeneric ? 'bg-amber-400' : 'bg-brand-teal',
                      )}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="w-16 text-right font-semibold text-navy-900">
                    {e.count.toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 text-xs text-slate-600">
        <p>
          Generic edges carry untyped relation labels (<code>related_to</code>,{' '}
          <code>drives</code>). The <strong>Retype Generic Edges</strong> action
          queues an LLM job that re-classifies each generic edge into a precise
          semantic type from the canonical guide (e.g.{' '}
          <code>precedes_in_journey</code>, <code>substitutes_for</code>,{' '}
          <code>amplifies</code>). The job runs in the background — counts
          refresh automatically a few seconds after you click.
        </p>
      </div>
    </div>
  );
}
